import { type TriggerProps } from '@atlaskit/popup';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { colors } from '@atlaskit/theme';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { Position } from '@databases/context-menu/type';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { DragDirection, DragType } from '@databases/drag';
import { EditMode, getFieldType } from '@databases/fields';
import { usePresence } from '@databases/presence';
import { type HoverProps, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectCurrentSelectionMode,
  selectIsHoverDeleteEntriesButton,
  selectIsHoverDeleteFieldsButton,
  selectIsSoftSelectEnabled,
  selectIsValueSelected,
  selectSelectedAdjacentCells,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { getService } from '@databases/service-container';
import { SELECTED_BORDER_WIDTH } from '@databases/styles';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectValueIdFromThreadIdOptional } from '@databases/utils/redux/comments/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getValueId } from '@databases/utils/utils';
import DatabaseValueInlineEdit from '@databases/value/DatabaseValueInlineEdit';
import VisibilityManager from '@databases/visibility-manager';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, {
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { getHeaderCellAtIndex } from '../../../table';

import AddEntryDot from './AddEntryDot';
import AddFieldDot from './AddFieldDot';
import DragCaret from './DragCaret';

const DragToFillIndicator = styled.div<{ position: Position }>`
  :after {
    content: ' ';
    position: absolute;
    ${({ position }) => (position === Position.BEFORE ? 'top: -3px;' : 'bottom: -3px;')}
    left: -${SELECTED_BORDER_WIDTH / 2}px;
    width: calc(100% + ${SELECTED_BORDER_WIDTH}px);
    height: 6px;
    background-color: ${colors.B200};
  }
`;

export type LegacyTableLayoutCellProps = {
  contentId: string;
  entryId: string;
  fieldId: string;
  rowIndex: number;
  colIndex: number;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;

  isFirstInRow: boolean;
  isLastInRow: boolean;
  isLastInColumn: boolean;
  isSelectionDisabled?: boolean;
  isInsertContentDisabled?: boolean;
  showAsDisabled?: boolean;
  addRowBeforeButtonHoverProps: HoverProps;
  addRowAfterButtonHoverProps: HoverProps;
  currentViewId: string;
  dropIndicatorEdge?: Edge | null;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  handleOnContextMenu?: (event: React.MouseEvent) => void;
  currentSortFieldId?: string;
};

/**
 * @deprecated Use TableLayoutCell
 *
 * During the `databasesSelectionsPerformance`, edits must be kept in parity
 * between TableLayoutCell and LegacyTableLayoutCell.
 */
const LegacyTableLayoutCell = React.memo(
  React.forwardRef<
    HTMLTableSectionElement,
    LegacyTableLayoutCellProps & Partial<TriggerProps>
  >(function LegacyTableLayoutCell(
    {
      contentId,
      entryId,
      fieldId,
      rowIndex,
      colIndex,
      isContentEditable,
      canEditContent,
      canEditFields,
      editingIds,
      setEditingIds,
      isFirstInRow,
      isLastInRow,
      isLastInColumn,
      isSelectionDisabled,
      isInsertContentDisabled,
      showAsDisabled,
      addRowBeforeButtonHoverProps,
      addRowAfterButtonHoverProps,
      currentViewId,
      dropIndicatorEdge,
      onIsSelectedChange,
      handleOnContextMenu,
      currentSortFieldId,
      ...rest
    },
    ref
  ) {
    const { focusedCommentThreadId, openThread } = useCommentStateContext();

    const [
      isFirstField,
      markAsDeleted,
      isSelected,
      isSoftSelected,
      selectedAdjacentCells,
      isCellSelected,
      showInsertColumnBefore,
      showInsertColumnAfter,
      showInsertRowBefore,
      showInsertRowAfter,
      canEditValue,
      isLastSelectedRow,
      isFirstSelectedRow,
      isLastSelectedCell,
      focusedThreadValueId,
    ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });

      const isSoftSelected = !isSelectionDisabled && selectIsSoftSelectEnabled(state);

      const isSelected =
        !isSelectionDisabled && selectIsValueSelected(state, { entryId, fieldId });

      const selectedFieldIds = isSelected
        ? selectSelectedFieldIds(state, {
            contentId,
          })
        : [];

      const selectedEntryIds = isSelected
        ? selectSelectedEntryIds(state, {
            contentId,
          })
        : [];

      const selectedAdjacentCells = isSelected
        ? selectSelectedAdjacentCells(state, {
            entryId,
            fieldId,
            contentId,
          })
        : undefined;
      const currentSelectionMode = selectCurrentSelectionMode(state, {
        contentId,
      });
      const isCellNotSelected = !(isSelected && currentSelectionMode === 'cell');
      const field = getDatabaseFieldRequired(database, fieldId);
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getDatabaseValue(database, field, entry);

      const canEditValue =
        getFieldType(field.type).editMode(
          {
            database: database,
            field,
            entry,
            value,
          },
          state
        ) !== EditMode.READ_ONLY;

      const isHoverDeleteEntriesButton =
        isSelected && selectIsHoverDeleteEntriesButton(state);
      const isHoverDeleteFieldsButton =
        isSelected && selectIsHoverDeleteFieldsButton(state);

      const isLastSelectedColumn =
        selectedFieldIds[selectedFieldIds.length - 1] === fieldId;
      const isLastSelectedRow = selectedEntryIds[selectedEntryIds.length - 1] === entryId;
      const isFirstSelectedRow = selectedEntryIds[0] === entryId;

      const isLastSelectedCell =
        isSelected &&
        ((currentSelectionMode === 'cell' && isLastSelectedRow && isLastSelectedColumn) ||
          (currentSelectionMode === 'row' && isLastInRow && isLastSelectedRow) ||
          (currentSelectionMode === 'column' && isLastInColumn && isLastSelectedColumn));

      const focusedThreadValueId = focusedCommentThreadId
        ? selectValueIdFromThreadIdOptional(state, {
            contentId,
            threadId: focusedCommentThreadId,
          })
        : undefined;

      return [
        fieldId === database.fields[0],
        isHoverDeleteEntriesButton || isHoverDeleteFieldsButton,
        isSelected,
        isSoftSelected,
        selectedAdjacentCells,
        !isCellNotSelected,
        isCellNotSelected && !selectedAdjacentCells?.left && !isInsertContentDisabled,
        isCellNotSelected && !selectedAdjacentCells?.right && !isInsertContentDisabled,
        isCellNotSelected && !selectedAdjacentCells?.top && !isInsertContentDisabled,
        isCellNotSelected && !selectedAdjacentCells?.bottom && !isInsertContentDisabled,
        canEditValue,
        isLastSelectedRow,
        isFirstSelectedRow,
        isLastSelectedCell,
        focusedThreadValueId,
      ] as const;
    });

    const valueId = useMemo(() => {
      return getValueId(entryId, fieldId);
    }, [entryId, fieldId]);

    const initialIsEditing = useMemo(
      () =>
        Array.from(editingIds).some(
          ({ entryId: editingEntryId, fieldId: editingFieldId }) => {
            return (
              (isFirstField &&
                editingEntryId === entryId &&
                editingFieldId === undefined) ||
              (editingEntryId === entryId && editingFieldId === fieldId)
            );
          }
        ),
      [editingIds, entryId, fieldId, isFirstField]
    );

    const cellRef = useRef<HTMLTableCellElement | null>(null);
    const justEnteredEditModeRef = useRef(false);
    useEffect(() => {
      const cellElement = cellRef.current;
      const visibilityManager = VisibilityManager.getInstance();
      if (cellElement && focusedThreadValueId === valueId) {
        visibilityManager.observe(cellElement, (el, unobserve) => {
          if (!el.isIntersecting) {
            // requestAnimationFrame is used to ensure that the scroll happens after the DOM update
            globalThis.requestAnimationFrame(() => {
              el.target.scrollIntoView({
                behavior: 'instant',
                block: 'center',
                inline: 'nearest',
              });
            });
            unobserve(el.target);
          }
        });
      }

      return () => {
        visibilityManager.disconnect();
      };
    }, [focusedThreadValueId, valueId]);

    const [isHoverInsertFieldDot, setIsHoverInsertFieldDot] = useState(false);
    const handleMouseEnterInsertField = useCallback(
      (position: Position) => {
        setIsHoverInsertFieldDot(true);
        const headerCellAtIndex = getHeaderCellAtIndex(cellRef?.current);
        headerCellAtIndex?.classList.add(
          position === Position.BEFORE
            ? TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE
            : TABLE_CLASSNAMES.INSERT_COLUMN_AFTER
        );
      },
      [cellRef]
    );

    const handleMouseLeaveInsertField = useCallback(
      (position: Position) => {
        setIsHoverInsertFieldDot(false);
        const headerCellAtIndex = getHeaderCellAtIndex(cellRef?.current);
        headerCellAtIndex?.classList.remove(
          position === Position.BEFORE
            ? TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE
            : TABLE_CLASSNAMES.INSERT_COLUMN_AFTER
        );
      },
      [cellRef]
    );

    const [dragDirectionWhenSelected, setDragDirectionWhenSelected] =
      useState<DragDirection | null>(null);

    useEffect(
      () =>
        monitorForElements({
          canMonitor: ({ source }) => source.data.type === DragType.FILL_VALUES,
          onDrag: ({ location }) => {
            const dragDirection =
              location.initial.input.clientY <= location.current.input.clientY
                ? DragDirection.DOWN
                : DragDirection.UP;
            if (isSelected) {
              setDragDirectionWhenSelected(dragDirection);
            }
          },
          onDrop: () => setDragDirectionWhenSelected(null),
        }),
      [contentId, isSelected]
    );

    const [isHoverCell, hoverProps, setIsHoverCell] = useHoverState();
    const selectionsActions = getService('SelectionsActions');

    useEffect(() => {
      if (isCellSelected && cellRef.current && !openThread.valueId) {
        cellRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, [isCellSelected, openThread]);

    const { isStreaming, operationType } = useDatabaseStreamState();
    const {
      entryIdSelectedForProcessing,
      isSingleFieldStreaming,
      currentEntryOperation,
    } = useSnapshot(aiFirstCreationStreamingState);
    const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
    const isEditProcessing =
      isStreaming &&
      isDbAiFirstCreationEnabled &&
      operationType === 'edit' &&
      entryId === entryIdSelectedForProcessing &&
      currentEntryOperation === 'edit';

    const { participants } = usePresence({
      grouping: { fieldId, entryId },
      componentId: 'LegacyTableLayoutCell',
      isEnabled: !isSelected,
    });

    const cellHasParticipants = participants.length > 0;

    const shouldShowDragCaret =
      ((!dragDirectionWhenSelected && isLastSelectedCell) ||
        (isHoverCell && !isSelected)) &&
      canEditValue &&
      !isHoverInsertFieldDot &&
      !isLastInColumn;

    return (
      <td
        {...rest}
        {...hoverProps}
        data-testid={'database-cell'}
        data-field-id={fieldId}
        data-entry-id={entryId}
        data-row-index={rowIndex}
        data-col-index={colIndex}
        onContextMenu={handleOnContextMenu}
        ref={composeRefs(cellRef, ref as Ref<HTMLTableCellElement>)}
        onFocus={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            justEnteredEditModeRef.current = true;
          }
        }}
        onBlur={(e) => {
          const shouldPreserveSelection =
            e.relatedTarget?.closest('#comment-threads-popup') ||
            e.relatedTarget?.closest('[data-canvas-target="comment-thread-dialog"]') ||
            e.relatedTarget?.closest('#ak-editor-textarea') ||
            e.target?.closest('#comment-threads-popup') ||
            e.target?.closest('#ak-editor-textarea');
          const focusStayingInCell = e.currentTarget.contains(e.relatedTarget);
          if (shouldPreserveSelection || focusStayingInCell) {
            return;
          }
          if ((e.target as HTMLElement).hasAttribute('data-inline-edit-read-view')) {
            return;
          }
          if (justEnteredEditModeRef.current) {
            justEnteredEditModeRef.current = false;
            return;
          }
          selectionsActions.deselectAll();
        }}
        className={classNames([
          {
            [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
            [TABLE_CLASSNAMES.IS_SOFT_SELECTED]: isSoftSelected,
            [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled,
            [TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING]:
              isEditProcessing && !isSingleFieldStreaming,
          },
        ])}
      >
        {!isFirstInRow && showInsertColumnBefore && isHoverCell && (
          <AddFieldDot
            contentId={contentId}
            fieldId={fieldId}
            position={Position.BEFORE}
            canEditFields={canEditFields}
            hoverProps={{
              onMouseEnter: () => handleMouseEnterInsertField(Position.BEFORE),
              onMouseLeave: () => handleMouseLeaveInsertField(Position.BEFORE),
            }}
            currentViewId={currentViewId}
          />
        )}
        {showInsertRowBefore && isHoverCell && !cellHasParticipants && (
          <AddEntryDot
            contentId={contentId}
            entryId={entryId}
            fieldId={fieldId}
            position={Position.BEFORE}
            canEditContent={canEditContent}
            hoverProps={addRowBeforeButtonHoverProps}
            setEditingIds={setEditingIds}
          />
        )}
        <DatabaseValueInlineEdit
          contentId={contentId}
          fieldId={fieldId}
          entryId={entryId}
          initialIsEditing={initialIsEditing}
          setEditingIds={setEditingIds}
          isReadonly={!canEditContent}
          isContentEditable={isContentEditable}
          isSelected={isSelected && !showAsDisabled}
          onIsSelectedChange={onIsSelectedChange}
          isFirstInRow={isFirstInRow}
          matchHeaderPadding={true}
          selectedAdjacentCells={selectedAdjacentCells}
          isLastInColumn={isLastInColumn}
          enableSelection
          markAsDeleted={markAsDeleted}
          isHoverCell={isHoverCell}
          setIsHoverCell={setIsHoverCell}
          currentSortFieldId={currentSortFieldId}
        />
        {showInsertRowAfter && isHoverCell && (
          <AddEntryDot
            contentId={contentId}
            entryId={entryId}
            fieldId={fieldId}
            position={Position.AFTER}
            canEditContent={canEditContent}
            hoverProps={addRowAfterButtonHoverProps}
            setEditingIds={setEditingIds}
          />
        )}
        {showInsertColumnAfter && isHoverCell && (
          <AddFieldDot
            contentId={contentId}
            fieldId={fieldId}
            position={Position.AFTER}
            canEditFields={canEditFields}
            hoverProps={{
              onMouseEnter: () => handleMouseEnterInsertField(Position.AFTER),
              onMouseLeave: () => handleMouseLeaveInsertField(Position.AFTER),
            }}
            currentViewId={currentViewId}
          />
        )}
        {shouldShowDragCaret && (
          <DragCaret
            contentId={contentId}
            canEditContent={canEditContent}
            isSelected={isSelected}
            fieldId={fieldId}
            entryId={entryId}
            isDeletionActive={markAsDeleted}
            onIsSelectedChange={onIsSelectedChange}
          />
        )}
        {isSelected &&
          dragDirectionWhenSelected === DragDirection.UP &&
          isFirstSelectedRow && <DragToFillIndicator position={Position.BEFORE} />}
        {isSelected &&
          dragDirectionWhenSelected === DragDirection.DOWN &&
          isLastSelectedRow && <DragToFillIndicator position={Position.AFTER} />}
        {dropIndicatorEdge && <DropIndicator edge={dropIndicatorEdge} />}
      </td>
    );
  })
);

export default LegacyTableLayoutCell;
